package com.ragnorak.clanybackoffice.ui.screens

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.FilterList
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.clany.backoffice.model.*

@Composable
fun UsersScreen(
    viewModel: UsersViewModel,
    onEditUser: (UserResponse) -> Unit = {}
) {
    val state = viewModel.uiState

    Box(modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier.fillMaxSize().padding(16.dp)
        ) {
            // Header
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    Icons.Default.Person,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(28.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    "User Management",
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold
                )
            }
            Spacer(modifier = Modifier.height(16.dp))

            // Search bar + filter toggle button
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OutlinedTextField(
                    value = state.searchQuery,
                    onValueChange = { viewModel.onSearchQueryChanged(it) },
                    placeholder = { Text("Search users...") },
                    leadingIcon = {
                        Icon(Icons.Default.Search, contentDescription = "Search")
                    },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.weight(1f),
                    enabled = !state.isFilterActive
                )
                BadgedBox(
                    badge = {
                        if (state.isFilterActive) {
                            Badge(
                                containerColor = MaterialTheme.colorScheme.primary
                            )
                        }
                    }
                ) {
                    OutlinedButton(
                        onClick = { viewModel.toggleFilterPanel() },
                        colors = if (state.isFilterActive || state.isFilterPanelOpen)
                            ButtonDefaults.outlinedButtonColors(
                                containerColor = MaterialTheme.colorScheme.primaryContainer
                            )
                        else ButtonDefaults.outlinedButtonColors()
                    ) {
                        Icon(
                            Icons.Default.FilterList,
                            contentDescription = "Filtros avanzados",
                            modifier = Modifier.size(18.dp)
                        )
                        Spacer(modifier = Modifier.width(4.dp))
                        Text("Filtros avanzados")
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))

            // Active filter indicator
            if (state.isFilterActive) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        "Filtro activo",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.primary,
                        fontWeight = FontWeight.SemiBold
                    )
                    TextButton(
                        onClick = { viewModel.clearFilter() },
                        colors = ButtonDefaults.textButtonColors(
                            contentColor = MaterialTheme.colorScheme.error
                        )
                    ) {
                        Icon(Icons.Default.Close, contentDescription = null, modifier = Modifier.size(16.dp))
                        Spacer(modifier = Modifier.width(4.dp))
                        Text("Limpiar filtro")
                    }
                }
            }

            // Advanced filter panel
            AnimatedVisibility(
                visible = state.isFilterPanelOpen,
                enter = expandVertically(),
                exit = shrinkVertically()
            ) {
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp)
                            .verticalScroll(rememberScrollState()),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Text(
                            "Filtros avanzados",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold
                        )
                        Spacer(modifier = Modifier.height(4.dp))

                        val fs = state.filterState

                        // 1. Perfil de usuario
                        FilterSection(title = "Perfil de usuario") {
                            FilterLabel("Tipo de usuario")
                            CheckboxRow(
                                options = listOf("FREE", "PREMIUM", "TESTER"),
                                labels = mapOf("FREE" to "Gratuito", "PREMIUM" to "Premium", "TESTER" to "Tester"),
                                selected = fs.selectedUserTypes,
                                onToggle = { viewModel.toggleUserType(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Estado del usuario")
                            CheckboxRow(
                                options = listOf("ACTIVE", "DELETED"),
                                labels = mapOf("ACTIVE" to "Activo", "DELETED" to "Eliminado"),
                                selected = fs.selectedUserStatuses,
                                onToggle = { viewModel.toggleUserStatus(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Idiomas (separados por coma, p.ej. es, en-US)")
                            OutlinedTextField(
                                value = fs.localesRaw,
                                onValueChange = { viewModel.updateLocalesRaw(it) },
                                placeholder = { Text("es, en-US, fr") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Proveedor de autenticación")
                            CheckboxRow(
                                options = listOf("EMAIL", "GOOGLE", "APPLE"),
                                labels = mapOf("EMAIL" to "Email", "GOOGLE" to "Google", "APPLE" to "Apple"),
                                selected = fs.selectedAuthProviders,
                                onToggle = { viewModel.toggleAuthProvider(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Versión de la app (separadas por coma)")
                            OutlinedTextField(
                                value = fs.appVersionsRaw,
                                onValueChange = { viewModel.updateAppVersionsRaw(it) },
                                placeholder = { Text("1.5.0, 1.4.2") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Anonimizado")
                            TriStateRow(current = fs.isAnonymized, onSelect = { viewModel.setIsAnonymized(it) })
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Debe actualizar TyC")
                            TriStateRow(current = fs.shouldUpdateTyc, onSelect = { viewModel.setShouldUpdateTyc(it) })
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Tiene miembro vinculado")
                            TriStateRow(current = fs.hasLinkedMember, onSelect = { viewModel.setHasLinkedMember(it) })
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Creado después de (YYYY-MM-DD)")
                            OutlinedTextField(
                                value = fs.createdAfter,
                                onValueChange = { viewModel.updateCreatedAfter(it) },
                                placeholder = { Text("2023-01-01") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Creado antes de (YYYY-MM-DD)")
                            OutlinedTextField(
                                value = fs.createdBefore,
                                onValueChange = { viewModel.updateCreatedBefore(it) },
                                placeholder = { Text("2024-12-31") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Último acceso después de (YYYY-MM-DD)")
                            OutlinedTextField(
                                value = fs.lastActiveAfter,
                                onValueChange = { viewModel.updateLastActiveAfter(it) },
                                placeholder = { Text("2024-01-01") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Último acceso antes de (YYYY-MM-DD)")
                            OutlinedTextField(
                                value = fs.lastActiveBefore,
                                onValueChange = { viewModel.updateLastActiveBefore(it) },
                                placeholder = { Text("2024-12-31") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                        }

                        // 2. Dispositivos
                        FilterSection(title = "Dispositivos") {
                            FilterLabel("Plataforma")
                            CheckboxRow(
                                options = listOf("ANDROID", "IOS"),
                                labels = mapOf("ANDROID" to "Android", "IOS" to "iOS"),
                                selected = fs.selectedPlatforms,
                                onToggle = { viewModel.togglePlatform(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Tiene tokens de dispositivo")
                            TriStateRow(current = fs.hasDeviceTokens, onSelect = { viewModel.setHasDeviceTokens(it) })
                        }

                        // 3. Clanes / Cuentas
                        FilterSection(title = "Clanes / Cuentas") {
                            FilterLabel("Tiene cuentas")
                            TriStateRow(current = fs.hasAccounts, onSelect = { viewModel.setHasAccounts(it) })
                            Spacer(modifier = Modifier.height(8.dp))

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Mín. cuentas")
                                    OutlinedTextField(
                                        value = fs.minAccountCount,
                                        onValueChange = { viewModel.updateMinAccountCount(it) },
                                        placeholder = { Text("0") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Máx. cuentas")
                                    OutlinedTextField(
                                        value = fs.maxAccountCount,
                                        onValueChange = { viewModel.updateMaxAccountCount(it) },
                                        placeholder = { Text("10") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                            }
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Rol en la cuenta")
                            CheckboxRow(
                                options = listOf("OWNER", "ADMIN", "MEMBER"),
                                labels = mapOf("OWNER" to "Propietario", "ADMIN" to "Admin", "MEMBER" to "Miembro"),
                                selected = fs.selectedAccountRoles,
                                onToggle = { viewModel.toggleAccountRole(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Estado del usuario en la cuenta")
                            CheckboxRow(
                                options = listOf("ACTIVE", "INACTIVE", "PENDING"),
                                labels = mapOf("ACTIVE" to "Activo", "INACTIVE" to "Inactivo", "PENDING" to "Pendiente"),
                                selected = fs.selectedAccountUserStatuses,
                                onToggle = { viewModel.toggleAccountUserStatus(it) }
                            )
                        }

                        // 4. Suscripciones
                        FilterSection(title = "Suscripciones") {
                            FilterLabel("Tiene suscripción activa")
                            TriStateRow(current = fs.hasActiveSubscription, onSelect = { viewModel.setHasActiveSubscription(it) })
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Estado de la suscripción")
                            CheckboxRow(
                                options = listOf("INITIAL_PURCHASE", "RENEWAL", "EXPIRATION", "CANCELLATION"),
                                labels = mapOf(
                                    "INITIAL_PURCHASE" to "Compra inicial",
                                    "RENEWAL" to "Renovación",
                                    "EXPIRATION" to "Expiración",
                                    "CANCELLATION" to "Cancelación"
                                ),
                                selected = fs.selectedSubscriptionStatuses,
                                onToggle = { viewModel.toggleSubscriptionStatus(it) }
                            )
                        }

                        // 5. Miembros
                        FilterSection(title = "Miembros") {
                            FilterLabel("Es miembro")
                            TriStateRow(current = fs.isMember, onSelect = { viewModel.setIsMember(it) })
                            Spacer(modifier = Modifier.height(8.dp))

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Mín. miembros")
                                    OutlinedTextField(
                                        value = fs.minMemberCount,
                                        onValueChange = { viewModel.updateMinMemberCount(it) },
                                        placeholder = { Text("0") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Máx. miembros")
                                    OutlinedTextField(
                                        value = fs.maxMemberCount,
                                        onValueChange = { viewModel.updateMaxMemberCount(it) },
                                        placeholder = { Text("100") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                            }
                        }

                        // 6. Eventos
                        FilterSection(title = "Eventos") {
                            FilterLabel("Tiene eventos")
                            TriStateRow(current = fs.hasEvents, onSelect = { viewModel.setHasEvents(it) })
                            Spacer(modifier = Modifier.height(8.dp))

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Mín. eventos")
                                    OutlinedTextField(
                                        value = fs.minEventCount,
                                        onValueChange = { viewModel.updateMinEventCount(it) },
                                        placeholder = { Text("0") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Máx. eventos")
                                    OutlinedTextField(
                                        value = fs.maxEventCount,
                                        onValueChange = { viewModel.updateMaxEventCount(it) },
                                        placeholder = { Text("100") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                            }
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Tipo de evento")
                            CheckboxRow(
                                options = listOf("SCHOOL", "WORK", "HEALTH", "PERSONAL", "FAMILY", "OTHER", "HOME", "SPECIAL", "REMINDER"),
                                labels = mapOf(
                                    "SCHOOL" to "Escuela",
                                    "WORK" to "Trabajo",
                                    "HEALTH" to "Salud",
                                    "PERSONAL" to "Personal",
                                    "FAMILY" to "Familia",
                                    "OTHER" to "Otro",
                                    "HOME" to "Hogar",
                                    "SPECIAL" to "Especial",
                                    "REMINDER" to "Recordatorio"
                                ),
                                selected = fs.selectedEventTypes,
                                onToggle = { viewModel.toggleEventType(it) }
                            )
                        }

                        // 7. Invitaciones
                        FilterSection(title = "Invitaciones") {
                            FilterLabel("Ha enviado invitaciones")
                            TriStateRow(current = fs.hasSentInvitations, onSelect = { viewModel.setHasSentInvitations(it) })
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Ha recibido invitaciones")
                            TriStateRow(current = fs.hasReceivedInvitations, onSelect = { viewModel.setHasReceivedInvitations(it) })
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Estado de la invitación")
                            CheckboxRow(
                                options = listOf("PENDING", "ACCEPTED", "REJECTED", "EXPIRED"),
                                labels = mapOf(
                                    "PENDING" to "Pendiente",
                                    "ACCEPTED" to "Aceptada",
                                    "REJECTED" to "Rechazada",
                                    "EXPIRED" to "Expirada"
                                ),
                                selected = fs.selectedInvitationStatuses,
                                onToggle = { viewModel.toggleInvitationStatus(it) }
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Mín. inv. enviadas")
                                    OutlinedTextField(
                                        value = fs.minSentInvitations,
                                        onValueChange = { viewModel.updateMinSentInvitations(it) },
                                        placeholder = { Text("0") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Máx. inv. enviadas")
                                    OutlinedTextField(
                                        value = fs.maxSentInvitations,
                                        onValueChange = { viewModel.updateMaxSentInvitations(it) },
                                        placeholder = { Text("100") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                            }
                            Spacer(modifier = Modifier.height(8.dp))

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Mín. inv. recibidas")
                                    OutlinedTextField(
                                        value = fs.minReceivedInvitations,
                                        onValueChange = { viewModel.updateMinReceivedInvitations(it) },
                                        placeholder = { Text("0") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                                Column(modifier = Modifier.weight(1f)) {
                                    FilterLabel("Máx. inv. recibidas")
                                    OutlinedTextField(
                                        value = fs.maxReceivedInvitations,
                                        onValueChange = { viewModel.updateMaxReceivedInvitations(it) },
                                        placeholder = { Text("100") },
                                        modifier = Modifier.fillMaxWidth(),
                                        singleLine = true
                                    )
                                }
                            }
                        }

                        // 8. IDs específicos
                        FilterSection(title = "IDs específicos") {
                            FilterLabel("Incluir IDs de usuario (uno por línea)")
                            OutlinedTextField(
                                value = fs.includeUserIdsRaw,
                                onValueChange = { viewModel.updateIncludeUserIdsRaw(it) },
                                placeholder = { Text("user_id_1\nuser_id_2") },
                                modifier = Modifier.fillMaxWidth(),
                                minLines = 3,
                                maxLines = 6
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            FilterLabel("Excluir IDs de usuario (uno por línea)")
                            OutlinedTextField(
                                value = fs.excludeUserIdsRaw,
                                onValueChange = { viewModel.updateExcludeUserIdsRaw(it) },
                                placeholder = { Text("user_id_1\nuser_id_2") },
                                modifier = Modifier.fillMaxWidth(),
                                minLines = 3,
                                maxLines = 6
                            )
                        }

                        Spacer(modifier = Modifier.height(8.dp))

                        // Action buttons
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            OutlinedButton(
                                onClick = { viewModel.clearFilter() },
                                modifier = Modifier.weight(1f),
                                colors = ButtonDefaults.outlinedButtonColors(
                                    contentColor = MaterialTheme.colorScheme.error
                                )
                            ) {
                                Text("Limpiar")
                            }
                            Button(
                                onClick = { viewModel.applyFilter() },
                                modifier = Modifier.weight(2f)
                            ) {
                                Text("Aplicar filtro")
                            }
                        }
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
            }

            if (state.isLoading) {
                LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
            }

            if (state.error != null) {
                Card(
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer
                    ),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        state.error,
                        color = MaterialTheme.colorScheme.onErrorContainer,
                        modifier = Modifier.padding(12.dp)
                    )
                }
                Spacer(modifier = Modifier.height(8.dp))
            }

            UserTable(
                users = state.users,
                onView = { viewModel.onViewUser(it) },
                onEdit = { viewModel.onEditUser(it) },
                onDelete = { viewModel.onDeleteUser(it.id) },
                modifier = Modifier.weight(1f)
            )

            // Pagination Controls
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                FilledTonalButton(
                    onClick = { viewModel.loadUsers(state.page - 1) },
                    enabled = state.page > 1
                ) {
                    Icon(
                        Icons.AutoMirrored.Filled.ArrowBack,
                        contentDescription = "Previous",
                        modifier = Modifier.size(18.dp)
                    )
                    Spacer(modifier = Modifier.width(4.dp))
                    Text("Previous")
                }
                Spacer(modifier = Modifier.width(16.dp))
                Text(
                    "Page ${state.page}",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.width(16.dp))
                FilledTonalButton(
                    onClick = { viewModel.loadUsers(state.page + 1) },
                    enabled = state.hasMore
                ) {
                    Text("Next")
                    Spacer(modifier = Modifier.width(4.dp))
                    Icon(
                        Icons.AutoMirrored.Filled.ArrowForward,
                        contentDescription = "Next",
                        modifier = Modifier.size(18.dp)
                    )
                }
            }
        }

        // Edit dialog overlay
        state.editingUser?.let { user ->
            EditUserOverlay(
                user = user,
                onDismiss = { viewModel.dismissEdit() },
                onConfirm = { updatedUser -> viewModel.updateUser(updatedUser) }
            )
        }

        // View detail dialog overlay
        state.viewingUser?.let { user ->
            UserDetailOverlay(
                user = user,
                onDismiss = { viewModel.dismissView() }
            )
        }

        // Delete confirmation overlay
        state.deletingUserId?.let { _ ->
            DeleteUserOverlay(
                onConfirm = { viewModel.confirmDelete() },
                onDismiss = { viewModel.dismissDelete() }
            )
        }
    }
}

@Composable
fun DeleteUserOverlay(
    onConfirm: () -> Unit,
    onDismiss: () -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.5f))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null
            ) { onDismiss() },
        contentAlignment = Alignment.Center
    ) {
        Card(
            modifier = Modifier
                .width(400.dp)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null
                ) { /* consume clicks */ },
            shape = RoundedCornerShape(16.dp),
            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
        ) {
            Column(
                modifier = Modifier.padding(24.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Icon(
                    Icons.Default.Delete,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.error,
                    modifier = Modifier.size(40.dp)
                )
                Spacer(modifier = Modifier.height(16.dp))
                Text(
                    "Delete User",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    "Are you sure you want to delete this user? This action cannot be undone.",
                    style = MaterialTheme.typography.bodyMedium
                )
                Spacer(modifier = Modifier.height(24.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.End
                ) {
                    TextButton(onClick = onDismiss) {
                        Text("Cancel")
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                    Button(
                        onClick = onConfirm,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = MaterialTheme.colorScheme.error
                        )
                    ) {
                        Text("Delete")
                    }
                }
            }
        }
    }
}

@Composable
fun UserTable(
    users: List<UserResponse>,
    onView: (UserResponse) -> Unit,
    onEdit: (UserResponse) -> Unit,
    onDelete: (UserResponse) -> Unit,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier,
        shape = RoundedCornerShape(12.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column {
            // Header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(MaterialTheme.colorScheme.primaryContainer)
                    .padding(12.dp)
            ) {
                Text("Name", modifier = Modifier.weight(2f), fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onPrimaryContainer)
                Text("Email", modifier = Modifier.weight(2f), fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onPrimaryContainer)
                Text("Type", modifier = Modifier.weight(1f), fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onPrimaryContainer)
                Text("Actions", modifier = Modifier.weight(1f), fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onPrimaryContainer)
            }
            HorizontalDivider()
            LazyColumn(modifier = Modifier.fillMaxSize()) {
                items(users) { user ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 12.dp, vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text("${user.firstName} ${user.lastName}", modifier = Modifier.weight(2f))
                        Text(user.email, modifier = Modifier.weight(2f))
                        Text(user.type.name, modifier = Modifier.weight(1f))

                        Row(modifier = Modifier.weight(1f)) {
                            IconButton(onClick = { onView(user) }) {
                                Icon(
                                    Icons.Default.Info,
                                    contentDescription = "View Details",
                                    tint = MaterialTheme.colorScheme.secondary,
                                    modifier = Modifier.size(20.dp)
                                )
                            }
                            IconButton(onClick = { onEdit(user) }) {
                                Icon(
                                    Icons.Default.Edit,
                                    contentDescription = "Edit",
                                    tint = MaterialTheme.colorScheme.primary,
                                    modifier = Modifier.size(20.dp)
                                )
                            }
                            IconButton(onClick = { onDelete(user) }) {
                                Icon(
                                    Icons.Default.Delete,
                                    contentDescription = "Delete",
                                    tint = MaterialTheme.colorScheme.error,
                                    modifier = Modifier.size(20.dp)
                                )
                            }
                        }
                    }
                    HorizontalDivider()
                }
            }
        }
    }
}

@Composable
fun EditUserOverlay(
    user: UserResponse,
    onDismiss: () -> Unit,
    onConfirm: (UserResponse) -> Unit
) {
    var firstName by remember { mutableStateOf(user.firstName) }
    var lastName by remember { mutableStateOf(user.lastName) }
    var locale by remember { mutableStateOf(user.locale) }
    var linkedMemberId by remember { mutableStateOf(user.linkedMemberId ?: "") }
    var pictureUrl by remember { mutableStateOf(user.pictureUrl ?: "") }
    var shouldUpdateTyc by remember { mutableStateOf(user.shouldUpdateTyc) }
    var selectedType by remember { mutableStateOf(user.type) }
    var typeDropdownExpanded by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.5f))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null
            ) { onDismiss() },
        contentAlignment = Alignment.Center
    ) {
        Card(
            modifier = Modifier
                .width(450.dp)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null
                ) { /* consume clicks so they don't dismiss */ },
            shape = RoundedCornerShape(16.dp),
            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(24.dp)
                    .verticalScroll(rememberScrollState())
            ) {
                // Header
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            Icons.Default.Edit,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.primary
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            "Edit User",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    IconButton(onClick = onDismiss) {
                        Icon(Icons.Default.Close, contentDescription = "Close")
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))

                Spacer(modifier = Modifier.height(16.dp))

                // Editable fields
                Text(
                    "Editable Fields",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.primary
                )
                Spacer(modifier = Modifier.height(8.dp))

                OutlinedTextField(
                    value = firstName,
                    onValueChange = { firstName = it },
                    label = { Text("First Name") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(8.dp)
                )
                Spacer(modifier = Modifier.height(8.dp))

                OutlinedTextField(
                    value = lastName,
                    onValueChange = { lastName = it },
                    label = { Text("Last Name") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(8.dp)
                )
                Spacer(modifier = Modifier.height(8.dp))

                // Type dropdown
                Box(modifier = Modifier.fillMaxWidth()) {
                    OutlinedTextField(
                        value = selectedType.name,
                        onValueChange = {},
                        label = { Text("Type") },
                        readOnly = true,
                        trailingIcon = {
                            IconButton(onClick = { typeDropdownExpanded = true }) {
                                Icon(Icons.Default.ArrowDropDown, contentDescription = "Select type")
                            }
                        },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(8.dp)
                    )
                    DropdownMenu(
                        expanded = typeDropdownExpanded,
                        onDismissRequest = { typeDropdownExpanded = false }
                    ) {
                        UserType.entries.forEach { userType ->
                            DropdownMenuItem(
                                text = { Text(userType.label) },
                                onClick = {
                                    selectedType = userType
                                    typeDropdownExpanded = false
                                }
                            )
                        }
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))

                OutlinedTextField(
                    value = locale,
                    onValueChange = { locale = it },
                    label = { Text("Locale") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(8.dp)
                )
                Spacer(modifier = Modifier.height(8.dp))

                OutlinedTextField(
                    value = linkedMemberId,
                    onValueChange = { linkedMemberId = it },
                    label = { Text("Linked Member ID") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(8.dp)
                )
                Spacer(modifier = Modifier.height(8.dp))

                OutlinedTextField(
                    value = pictureUrl,
                    onValueChange = { pictureUrl = it },
                    label = { Text("Picture URL") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(8.dp)
                )
                Spacer(modifier = Modifier.height(8.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text("Should Update TyC")
                    Switch(
                        checked = shouldUpdateTyc,
                        onCheckedChange = { shouldUpdateTyc = it }
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))

                // Action buttons
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.End
                ) {
                    TextButton(onClick = onDismiss) {
                        Text("Cancel")
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                    Button(
                        onClick = {
                            onConfirm(
                                user.copy(
                                    firstName = firstName,
                                    lastName = lastName,
                                    type = selectedType,
                                    locale = locale,
                                    linkedMemberId = linkedMemberId.ifBlank { null },
                                    pictureUrl = pictureUrl.ifBlank { null },
                                    shouldUpdateTyc = shouldUpdateTyc
                                )
                            )
                        }
                    ) {
                        Text("Save")
                    }
                }
            }
        }
    }
}

@Composable
fun UserDetailOverlay(
    user: UserResponse,
    onDismiss: () -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.5f))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null
            ) { onDismiss() },
        contentAlignment = Alignment.Center
    ) {
        Card(
            modifier = Modifier
                .width(450.dp)
                .fillMaxHeight(0.9f)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null
                ) { },
            shape = RoundedCornerShape(16.dp),
            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(24.dp)
            ) {
                // Header
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            Icons.Default.Info,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.secondary
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            "User Details",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    IconButton(onClick = onDismiss) {
                        Icon(Icons.Default.Close, contentDescription = "Close")
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))
                HorizontalDivider()
                Spacer(modifier = Modifier.height(16.dp))

                LazyColumn(modifier = Modifier.fillMaxSize()) {
                    item {
                        SelectionContainer {
                        Column {
                        DetailRow("User ID", user.id)
                        DetailRow("Username", user.username)
                        DetailRow("Email", user.email)
                        DetailRow("First Name", user.firstName)
                        DetailRow("Last Name", user.lastName)
                        DetailRow("Type", user.type.name)
                        DetailRow("Status", user.status.name)
                        DetailRow("Auth Provider", user.authProvider.name)
                        DetailRow("Locale", user.locale)
                        DetailRow("Linked Member ID", user.linkedMemberId ?: "None")
                        DetailRow("External ID", user.externalId ?: "None")
                        DetailRow("Created At", user.createdAt)
                        DetailRow("Updated At", user.updatedAt)
                        DetailRow("Last Active", user.lastActiveAt ?: "Never")
                        DetailRow("Should Update TyC", user.shouldUpdateTyc.toString())
                        DetailRow("Last TyC Notification", user.lastTycNotification ?: "Never")

                        if (user.isAnonymized) {
                            Spacer(modifier = Modifier.height(8.dp))
                            Text("USER IS ANONYMIZED", color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold)
                            Spacer(modifier = Modifier.height(8.dp))
                        }

                        Spacer(modifier = Modifier.height(16.dp))
                        Text("Device Tokens", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
                        Spacer(modifier = Modifier.height(8.dp))
                        if (user.deviceTokens.isEmpty()) {
                            Text("No device tokens.", style = MaterialTheme.typography.bodyMedium)
                        } else {
                            var expanded by remember { mutableStateOf(false) }
                            var selectedToken by remember { mutableStateOf(user.deviceTokens.firstOrNull()) }

                            Box(modifier = Modifier.fillMaxWidth()) {
                                OutlinedButton(onClick = { expanded = true }) {
                                    Text(selectedToken?.platform ?: "Select Platform")
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Icon(Icons.Default.ArrowDropDown, contentDescription = null)
                                }
                                DropdownMenu(
                                    expanded = expanded,
                                    onDismissRequest = { expanded = false }
                                ) {
                                    user.deviceTokens.forEach { token ->
                                        DropdownMenuItem(
                                            text = { Text(token.platform.ifEmpty { "Unknown Platform" }) },
                                            onClick = {
                                                selectedToken = token
                                                expanded = false
                                            }
                                        )
                                    }
                                }
                            }

                            selectedToken?.let { token ->
                                Spacer(modifier = Modifier.height(8.dp))
                                Card(
                                    modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
                                ) {
                                    Column(modifier = Modifier.padding(8.dp)) {
                                        Text("Token:", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                                        SelectionContainer {
                                            Text(token.notificationToken, style = MaterialTheme.typography.bodySmall)
                                        }
                                    }
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(16.dp))
                        Text("Accepted Terms", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
                        Spacer(modifier = Modifier.height(8.dp))
                        if (user.acceptedTerms.isEmpty()) {
                            Text("No terms accepted.", style = MaterialTheme.typography.bodyMedium)
                        } else {
                            user.acceptedTerms.forEach { term ->
                                Card(
                                    modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
                                ) {
                                    Column(modifier = Modifier.padding(8.dp)) {
                                        Text("Version: ${term.version}", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                                        Text("Accepted at: ${term.acceptedAt}", style = MaterialTheme.typography.bodySmall)
                                    }
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(16.dp))
                        } // Column
                        } // SelectionContainer
                    }
                }
            }
        }
    }
}

@Composable
private fun DetailRow(label: String, value: String) {
    Column(modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp)) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyLarge
        )
    }
}
